import { useEffect, useMemo, useRef } from 'react'
import { Navigate, Outlet, useLocation, matchPath } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { logout } from '@/redux/features/userSlice'
import { Loader } from '@/components/custom/loader'
import childRoutes from '@/routes'
import { usePermissions } from '@/context/permissions'
import { useGetPermissions } from '@/services/queries/user/use-get-permissions'
import { IUserPermission } from '@/types/user'
import { rememberRedirectAfterLogin } from '@/utils/redirect-after-login'

const dashboardStaticRoute: IUserPermission = {
  id: 12345676543223,
  module: {
    module_key: 'dashboard',
    id: 2345658123891,
    name: 'Dashboard',
    name_en: 'Dashboard',
    name_ru: 'Dashboard',
    name_uz: 'Dashboard',
    is_active: true,
  },
  is_granted: true,
  actions: [
    {
      action_key: 'list',
      action_value: true,
    },
    {
      action_key: 'retrieve',
      action_value: true,
    },
    {
      action_key: 'create',
      action_value: true,
    },
    {
      action_key: 'update',
      action_value: true,
    },
    {
      action_key: 'destroy',
      action_value: true,
    },
    {
      action_key: 'approve',
      action_value: true,
    },
    {
      action_key: 'reject',
      action_value: true,
    },
    {
      action_key: 'options',
      action_value: true,
    },
    {
      action_key: 'update_status',
      action_value: false,
    },
    {
      action_key: 'force_update',
      action_value: false,
    },
  ],
}

const ProtectedRoute = () => {
  const location = useLocation()
  const { pathname } = location

  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated)
  const {
    data: permissionsData,
    isLoading,
    isSuccess,
    isError,
  } = useGetPermissions()
  const { setPermissions, permissions } = usePermissions()

  const currentRoute = useMemo(() => {
    return childRoutes.find((r) => matchPath(r.path, pathname)) ?? null
  }, [pathname])

  useEffect(() => {
    if (isSuccess && permissionsData) {
      setPermissions([...permissionsData, dashboardStaticRoute])
    }
  }, [isSuccess, permissionsData, setPermissions])

  useEffect(() => {
    if (isError) {
      dispatch(logout())
    }
  }, [dispatch, isError])

  // Opening a dashboard link without a live session (expired/absent token)
  // bounces to sign-in. Remember the full URL — params included — so the login
  // form can send the user to the page they actually asked for instead of home.
  // Read on the first render only: later renders happen after the redirect.
  const entryPathRef = useRef(
    isAuthenticated
      ? null
      : `${location.pathname}${location.search}${location.hash}`
  )
  useEffect(() => {
    if (entryPathRef.current) {
      rememberRedirectAfterLogin(entryPathRef.current)
    }
  }, [])

  if (!isAuthenticated) {
    return <Navigate to='/sign-in' replace />
  }

  if (isError) {
    return <Navigate to='/sign-in' replace />
  }

  if (currentRoute === null) {
    return <Navigate to='/404' replace />
  }

  if (!permissions || isLoading) {
    return (
      <div className='flex h-svh items-center justify-center bg-background'>
        <Loader />
      </div>
    )
  }

  const permission = permissions?.find(
    (p) => p.module.module_key === currentRoute.uid
  )

  const isAllowed =
    permission?.is_granted ||
    permission?.actions.some(
      (a) => a.action_key === currentRoute.type && a.action_value
    )
  if (isAllowed) {
    return <Outlet />
  }

  return <Navigate to='/403' replace />
}

export default ProtectedRoute
